import React from 'react';
import { X, AlertCircle } from 'lucide-react';
import { useTranslation } from '../../../../contexts/I18nProvider';

interface SettingsModalProps {
  isOpen: boolean;
  enableRapidScanning: boolean;
  onEnableRapidScanningChange: (value: boolean) => void;
  /** TW 36413029: «Vis varer på lokasjon». Skjules når lokasjon er valgfri (Compass GO). */
  showItemsOnLocationAvailable: boolean;
  showItemsOnLocation: boolean;
  onShowItemsOnLocationChange: (value: boolean) => void;
  /** Feilmelding når en innstilling ikke kunne lagres på profilen. */
  saveError?: string | null;
  onClose: () => void;
}

interface SettingToggleProps {
  title: string;
  description: string;
  ariaLabel: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}

const SettingToggle: React.FC<SettingToggleProps> = ({ title, description, ariaLabel, checked, onChange }) => (
  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
    <div className="flex-1">
      <h4 className="text-sm font-medium text-gray-900">{title}</h4>
      <p className="text-xs sm:text-sm text-gray-500 mt-1">{description}</p>
    </div>
    <button
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-8 w-14 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
        checked ? 'bg-blue-600' : 'bg-gray-200'
      }`}
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
    >
      <span
        className={`pointer-events-none inline-block h-7 w-7 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
          checked ? 'translate-x-6 sm:translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  </div>
);

export const SettingsModal: React.FC<SettingsModalProps> = ({
  isOpen,
  enableRapidScanning,
  onEnableRapidScanningChange,
  showItemsOnLocationAvailable,
  showItemsOnLocation,
  onShowItemsOnLocationChange,
  saveError,
  onClose
}) => {
  const { t } = useTranslation();
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end tablet:items-center justify-center z-50 p-0 tablet:p-4">
      <div className="bg-white rounded-t-2xl tablet:rounded-lg max-w-md w-full tablet:h-auto h-auto animate-slide-up tablet:animate-none">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-base sm:text-lg font-semibold">{t('common.buttons.settings')}</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 p-2 min-h-[44px] min-w-[44px] flex items-center justify-center -mr-2"
            aria-label={t('common.buttons.close')}
          >
            <X size={20} />
          </button>
        </div>
        <div className="p-4 sm:p-6">
          <div className="space-y-5">
            <SettingToggle
              title={t('warehouse.modules.inventory.settings.rapidScanning')}
              description={t('warehouse.modules.inventory.settings.rapidScanningDescription')}
              ariaLabel={t('warehouse.modules.inventory.settings.toggleRapidScanning')}
              checked={enableRapidScanning}
              onChange={onEnableRapidScanningChange}
            />

            {showItemsOnLocationAvailable && (
              <SettingToggle
                title={t('warehouse.modules.inventory.settings.showItemsOnLocation', {}, 'Vis varer på lokasjon')}
                description={t('warehouse.modules.inventory.settings.showItemsOnLocationDescription', {}, 'Viser varene Compass har på lokasjonen du skanner, slik at du kan telle ned det som ikke finnes')}
                ariaLabel={t('warehouse.modules.inventory.settings.toggleShowItemsOnLocation', {}, 'Slå av/på visning av varer på lokasjon')}
                checked={showItemsOnLocation}
                onChange={onShowItemsOnLocationChange}
              />
            )}

            {saveError && (
              <div className="p-2 rounded flex items-start text-xs sm:text-sm bg-red-50 text-red-800">
                <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" />
                <span className="break-words">{saveError}</span>
              </div>
            )}
          </div>
        </div>
        <div className="p-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="w-full sm:w-auto px-4 py-2.5 sm:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm min-h-[44px]"
          >
            {t('common.buttons.close')}
          </button>
        </div>
      </div>
    </div>
  );
};
